<template>
  <div data-test="hire-contracts-timeline" class="tw-pt-5 tw-max-w-[65%]">
    <div v-if="loading" data-test="hire-contracts-loading" class="tw-flex tw-flex-col tw-gap-3">
      <Loading v-for="row in 3" :key="row" avatar />
    </div>

    <WdsProgress
      v-else-if="progressSteps.length > 0"
      class="expanded md:tw-pl-28"
      secondary
      sm
      :steps="progressSteps"
      :current="[]"
      :most-completed-step="[]"
      :failed-steps="[]"
    >
      <template #parent="{ mStep }">
        <HireContractRow
          :step="mStep.params as ContractHistoryStep"
          :can-edit="canEdit"
          :hire-active-status="hireActiveStatus"
        />
      </template>
    </WdsProgress>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { WdsProgress } from '@worksome/wds-components'
import { graphql, useFragment } from '@/gql'
import HireContractRow from '@/components/Hire/HireContractRow.vue'
import Loading from '@/components/Dashboard/Widgets/Utils/Loading.vue'
import { useHireRecruiter } from '@/components/Hire/composables/useHireRecruiter'
import { useAccountCapabilities } from '@/composables/useAccountCapabilities'
import { user } from '@/composables/useUser'
import { canEditHireContract } from '@/components/Hire/contractEditing'
import {
  buildContractHistory,
  type ContractBucket,
  type ContractHistoryEntry,
  type ContractHistoryStep,
} from '@/components/Hire/contractHistory'

const props = defineProps<{
  hireId: string
}>()

const { isCompany } = useAccountCapabilities()
const { viewerIsSupplier } = useHireRecruiter(props.hireId)

// Only the client can amend a contract; suppliers get the read-only view of
// every version, including the latest one.
const viewingAsClient = computed(() => isCompany.value && !viewerIsSupplier.value)

const HirePageContractsFragment = graphql(`
  fragment HirePageContractsType on Hire {
    id
    activeStatus
    contracts(first: 100) {
      data {
        id
        status
        startDate
        endDate
        rate
        rateType
        currency
        pdfUrl
        termsAcceptedAt
        workerAcceptedAt
        effectiveAt
        createdAt
      }
      paginatorInfo {
        total
      }
    }
  }
`)

const { result, loading } = useQuery(
  graphql(`
    query HirePageContractsData($id: ID!) {
      hire(id: $id) {
        ...HirePageContractsType
      }
    }
  `),
  () => ({ id: props.hireId }),
  { clientId: 'publicApi' },
)

const hire = computed(() => useFragment(HirePageContractsFragment, result.value?.hire))

const contracts = computed<ContractHistoryEntry[]>(
  () => (hire.value?.contracts?.data ?? []) as ContractHistoryEntry[],
)

const hireActiveStatus = computed(() => hire.value?.activeStatus ?? null)
const canEdit = computed(
  () =>
    viewingAsClient.value &&
    user.value.hasPermission('manage-contracts') &&
    canEditHireContract(hireActiveStatus.value),
)

const steps = computed(() => buildContractHistory(contracts.value))

function stepStatus(bucket: ContractBucket) {
  switch (bucket) {
    case 'active':
      return 'completed' as const
    case 'scheduled':
    case 'draft':
      return 'active' as const
    default:
      return 'incomplete' as const
  }
}

const progressSteps = computed(() =>
  steps.value.map((step: ContractHistoryStep) => ({
    name: `version-${step.versionNumber}`,
    url: '',
    params: step,
    status: stepStatus(step.bucket),
  })),
)
</script>

<style lang="scss" scoped>
:deep(.wds-progress-expanded) {
  @apply tw-flex-1;
}

:deep(.wds-main-step) {
  @apply tw-w-full;
}
</style>
